import { useParams, Link } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useUser } from "../hooks/useUser";
import { CollaborativeCrossword } from "../components/CollaborativeCrossword";
import { ShareLink } from "../components/ShareLink";
import { PlayerList } from "../components/PlayerList";
import { useMemo } from "react";

export function GamePage() {
  const { shareCode } = useParams<{ shareCode: string }>();
  const { user } = useUser();

  const gameData = useQuery(
    api.sessions.getByShareCode,
    shareCode ? { shareCode } : "skip"
  );

  // CRITICAL: Memoize these to prevent mycrossword from reinitializing on every Convex update
  // Must be called before any conditional returns (Rules of Hooks)
  const stablePuzzleData = useMemo(
    () => gameData?.puzzle?.data,
    [gameData?.puzzle?._id]
  );
  const stableInitialGrid = useMemo(
    () => gameData?.gameState?.grid,
    [gameData?.session?._id]
  );

  if (gameData === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading game...</div>
      </div>
    );
  }

  if (gameData === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Game Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            This game link may have expired or is invalid.
          </p>
          <Link
            to="/puzzles"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Start New Game
          </Link>
        </div>
      </div>
    );
  }

  const { session, puzzle, gameState } = gameData;

  if (!puzzle || !gameState || !stablePuzzleData || !stableInitialGrid || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">Error loading game data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-2"
                title="Home"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <span className="text-sm font-medium">Home</span>
              </Link>
              <div>
                <h1 className="text-xl font-bold">{puzzle.title}</h1>
                {puzzle.author && (
                  <p className="text-sm text-gray-600">by {puzzle.author}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <PlayerList sessionId={session._id} currentUserId={user.id} />
              <ShareLink shareCode={shareCode!} />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <CollaborativeCrossword
          sessionId={session._id}
          userId={user.id}
          displayName={user.displayName}
          puzzleData={stablePuzzleData}
          initialGrid={stableInitialGrid}
        />
      </main>
    </div>
  );
}
